/** What a filing's regime says about it, as three short groups under the readiness band: **the
 * reporting year**, **the dates** and, where the regime names a route for this one filing, **the
 * first report**. Labels and buttons only; every sentence is behind a button, in a drawer.
 *
 * **Nothing is typed here that is regulatory.** The year, its paragraph and its reason come from the
 * regime's year rule and the company's fiscal year end (`reporting_year`); the dates are the regime's
 * `watch_for` and `period` rows (`key_dates`); the routes are the regime's date-limited submission
 * rows (`alternatives`), and the sentence about them is the due-date rule's own text
 * (`due_date_basis`, which carries the rule's limitation). A regime that holds none of these prints
 * nothing, so a second market is rows and this file does not change.
 *
 * **It never decides.** Which route a company may take turns on facts about its past that EmitIQ
 * cannot see. The drawer says whose call that is in one line, the statement is drafted only after a
 * person confirms the fact it asserts (the same panel the submission pack uses), and a date is shown
 * with no countdown and no amount.
 */

import { CaretRight } from "@phosphor-icons/react";
import { useState } from "react";

import type { Obligation, Scope2Method } from "../../api/queries";
import { useSubmissionPack } from "../../api/submissionPack";
import { KeyDatesList } from "../../components/KeyDates";
import { ReportingYearNote } from "../../components/FilingYearNote";
import { Drawer } from "../../components/ui/Drawer";
import { InfoPopover } from "../../components/ui/InfoPopover";
import { shortDate } from "../../lib/format";
import { keyDateName, keyDateValue } from "../../lib/keyDates";
import { LetterPanel } from "../ReportsSubmission";
import styles from "./FilingFacts.module.css";

type Open = null | "year" | "dates" | { route: string };

function yearLabel(year: NonNullable<Obligation["reporting_year"]>): string {
  if (year.undetermined || !year.period_start || !year.period_end) return "Not determined";
  return `${shortDate(year.period_start)} to ${shortDate(year.period_end)}`;
}

function RouteBody({
  active,
  method,
  routeKey,
  canDraft,
  onOpenPack,
}: {
  active: Obligation;
  method: Scope2Method;
  routeKey: string;
  canDraft: boolean;
  onOpenPack: () => void;
}) {
  const route = active.alternatives.find((item) => item.key === routeKey);
  // The pack is read only for the route that has words to draft, and only once it is opened.
  const pack = useSubmissionPack(route?.is_statement ? active.obligation_id : undefined, method);
  if (!route) return null;
  const option = pack.data?.options.find((item) => item.key === routeKey);
  return (
    <div className={styles.body} data-testid="first-report-route" data-route={route.key}>
      <p className={styles.counsel} data-testid="first-report-counsel">
        Whether your company qualifies is for you and your counsel to decide.
      </p>
      <p>{active.due_date_basis}</p>
      <p>{route.detail}</p>
      {route.limitation ? <p>{route.limitation}</p> : null}
      <p>
        <a href={route.source_url} target="_blank" rel="noreferrer">
          {route.citation}
        </a>
      </p>
      {route.is_statement ? (
        pack.error ? (
          <p role="alert">The statement could not be loaded. Open the submission pack to draft it.</p>
        ) : option?.has_letter ? (
          <LetterPanel obligationId={active.obligation_id} option={option} canDraft={canDraft} />
        ) : pack.isPending ? (
          <p>Reading the statement.</p>
        ) : null
      ) : null}
      <button type="button" className={styles.link} onClick={onOpenPack}>
        Open the submission pack
      </button>
    </div>
  );
}

export function FilingFacts({
  active,
  method,
  canDraft,
  onOpenPack,
}: {
  active: Obligation;
  method: Scope2Method;
  canDraft: boolean;
  onOpenPack: () => void;
}) {
  const [open, setOpen] = useState<Open>(null);
  // Closed until asked for (design review 2026-11-03): the filing's job is its to-do list, and the
  // year, the dates and the first-report options are one row away, not a card above it.
  const [expanded, setExpanded] = useState(false);
  const year = active.reporting_year;
  const dates = active.key_dates;
  const routes = active.alternatives;
  if (!year && dates.length === 0 && routes.length === 0) return null;

  const close = () => setOpen(null);
  const openRoute = typeof open === "object" && open !== null ? open.route : null;
  const yearDifferent = year?.filing_matches === false;

  return (
    <section className={`card ${styles.facts}`} aria-label="Filing facts" data-testid="filing-facts">
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        onClick={() => setExpanded((was) => !was)}
        data-testid="facts-toggle"
      >
        <CaretRight size={12} weight="bold" aria-hidden="true" className={expanded ? styles.caretOpen : styles.caret} />
        Dates and first-report rules
      </button>
      {expanded && year ? (
        <div className={styles.group} data-testid="facts-year">
          <span className={styles.name}>Reporting year</span>
          <button
            type="button"
            className={`${styles.chip} ${yearDifferent || year.undetermined ? styles.warn : ""}`}
            onClick={() => setOpen("year")}
            data-testid="facts-year-button"
          >
            {yearLabel(year)}
            {yearDifferent ? " (not this filing)" : ""}
          </button>
        </div>
      ) : null}
      {expanded && dates.length > 0 ? (
        <div className={styles.group} data-testid="facts-dates">
          <span className={styles.name}>
            Dates <InfoPopover infoKey="filings.dates" />
          </span>
          {dates.map((item) => (
            <button
              key={item.key}
              type="button"
              className={styles.chip}
              onClick={() => setOpen("dates")}
              data-testid="facts-date"
              data-key={item.key}
            >
              {keyDateName(item)}
              {keyDateValue(item) ? ` · ${keyDateValue(item)}` : ""}
            </button>
          ))}
        </div>
      ) : null}
      {expanded && routes.length > 0 ? (
        <div className={styles.group} data-testid="facts-routes">
          <span className={styles.name}>
            First report <InfoPopover infoKey="filings.first-report" />
          </span>
          {routes.map((route) => (
            <button
              key={route.key}
              type="button"
              className={styles.chip}
              onClick={() => setOpen({ route: route.key })}
              data-testid="facts-route"
              data-key={route.key}
            >
              {route.label}
            </button>
          ))}
        </div>
      ) : null}

      <Drawer open={open === "year"} onClose={close} title="Reporting year" width="md">
        <ReportingYearNote year={year} />
      </Drawer>
      <Drawer open={open === "dates"} onClose={close} title="Dates" width="md">
        <KeyDatesList items={dates} />
      </Drawer>
      <Drawer open={openRoute !== null} onClose={close} title="First report" width="md">
        {openRoute !== null ? (
          <RouteBody
            active={active}
            method={method}
            routeKey={openRoute}
            canDraft={canDraft}
            onOpenPack={() => {
              close();
              onOpenPack();
            }}
          />
        ) : null}
      </Drawer>
    </section>
  );
}
